import {
  CameraAlt,
  Description,
  Add,
  BadgeOutlined,
  CalendarMonthOutlined,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  EmailOutlined,
  HomeOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CreateUserButton } from "../components/CreateUserButton";
import { ImpersonateIcon } from "../components/ImpersonateButton";
import { soulApi } from "../data/api/SoulApi";
import { v4 as uuidv4 } from "uuid";
import { soulManager } from "../data/managers/SoulManager";
import { CreditApplication } from "../data/models/applications/CreditApplication";
import { SoulDataModel, UpdateSoulInfoPayload } from "../data/models/Soul/SoulDataModel";
import { useAuth } from "../data/providers/AuthProvider";
import { fmtMoney } from "../utils/NumberTool";
import { useAllProfile } from "./hooks/useAllProfile";

const GREEN = "#00C853";
const DARK = "#0D1420";

async function loadProfile(): Promise<SoulDataModel[]> {
  const data = await soulManager.getAllSouls();
  if (!data) throw new Error("Error on loadProfile");
  return data!;
}

async function updateProfile(changes: Partial<SoulDataModel>) {
  const payload = new UpdateSoulInfoPayload(changes);
  const result = await soulManager.updateSoulInfoByAdmin(payload);
  if (!result) throw new Error("Error on updateProfile");
}

const getInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return ((parts[0][0] ?? "") + (parts[1][0] ?? "")).toUpperCase();
};

const AllProfilePage: React.FC = () => {
  const {
    profiles, loading, query, setQuery,
    editing, openEdit, closeEdit, saveEdit, saving,
    viewing, openView, closeView, refresh,
  } = useAllProfile({ loadProfiles: loadProfile, updateProfile });

  const roles = useMemo(() => ["USER", "ADM"], []);
  const { loginAsOther } = useAuth();

  const [userCredits, setUserCredits] = useState<CreditApplication[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [openNewCredit, setOpenNewCredit] = useState(false);
  const [selectedClient, setSelectedClient] = useState<SoulDataModel | null>(null);
  const [newCreditForm, setNewCreditForm] = useState<any>({
    amount: 0, termMonths: 12, purpose: "", interestRate: 6,
    accountBank: "", accountType: "", accountNumber: "",
    guarantorName: "", guarantorPhone: "",
    paymentType: "CAPITAL_INTEREST",
  });
  const [savingCredit, setSavingCredit] = useState(false);

  const loadUserCredits = async (document: string) => {
    try {
      setLoadingCredits(true);
      const all = await soulManager.getAllApplicationsByAdmin();
      const filtered = (all ?? []).filter((c) =>
        c.document === document &&
        (c.status === "APPROVED" || c.status === "FINISHED" || c.status === "PENDING")
      );
      setUserCredits(filtered);
    } finally {
      setLoadingCredits(false);
    }
  };

  const handleCrearCredito = async () => {
    if (!selectedClient) return;
    try {
      setSavingCredit(true);
      const id = uuidv4();
      const payload = {
        id,
        userId: selectedClient.userId,
        applicantName: selectedClient.fullName,
        document: selectedClient.document,
        documentType: selectedClient.documentType,
        status: "PENDING",
        ...newCreditForm,
        isConsentAvailable: true,
        consentPdfUploaded: "",
        guarantorId: "",
        guarantorDocId: "",
        createdAt: new Date().toISOString(),
      };
      await soulManager.createSoulApplication(payload);
      await loadUserCredits(selectedClient.document || "");
      setOpenNewCredit(false);
      setNewCreditForm({ amount: 0, termMonths: 12, purpose: "", interestRate: 6, accountBank: "", accountType: "", accountNumber: "", guarantorName: "", guarantorPhone: "", paymentType: "CAPITAL_INTEREST" });
    } catch (err) {
      console.error(err);
    } finally {
      setSavingCredit(false);
    }
  };

  const handleNuevoCredito = async (userId: string) => {
    try {
      setImpersonating(true);
      const result = await soulApi.getCustomTokenAsAdmin.request({ referencedUser: userId });
      if (!result?.isSuccess) throw new Error("Error");
      await loginAsOther(result.data?.token!);
      closeView();
      window.location.href = "/dashboard/applications";
    } catch (err) {
      console.error(err);
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

      {/* Buscador */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
        <TextField
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, documento o email..."
          fullWidth size="small"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "50px" } }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexShrink={0}>
          <CreateUserButton refreshList={refresh} />
        </Stack>
      </Stack>

      <Typography sx={{ fontSize: 13, color: "#8899AA", fontWeight: 500 }}>
        {profiles.length} {profiles.length === 1 ? "usuario" : "usuarios"} encontrados
      </Typography>

      {/* Lista */}
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress sx={{ color: GREEN }} />
          <Typography variant="body2" sx={{ mt: 2, color: "#8899AA" }}>Cargando perfiles…</Typography>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {profiles.map((p: any) => (
            <Grid item xs={12} sm={6} lg={4} key={p.id} sx={{ minWidth: 0 }}>
              <Card
                elevation={0}
                onClick={() => { openView(p); loadUserCredits(p.document || ""); }}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${p.isVerified ? GREEN + "44" : "#e0e0e0"}`,
                  borderLeft: `4px solid ${p.isVerified ? GREEN : "#e0e0e0"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { boxShadow: `0 4px 20px ${GREEN}18`, transform: "translateY(-2px)" },
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      <Avatar sx={{ width: 44, height: 44, bgcolor: p.isVerified ? `${GREEN}22` : "#f0f0f0", border: `2px solid ${p.isVerified ? GREEN : "#e0e0e0"}`, color: p.isVerified ? GREEN : "#8899AA", fontWeight: 700, fontSize: 15 }}>
                        {getInitials(p.fullName)}
                      </Avatar>
                      {p.isVerified && (
                        <CheckCircleIcon sx={{ position: "absolute", bottom: -2, right: -2, fontSize: 16, color: GREEN, bgcolor: "white", borderRadius: "50%" }} />
                      )}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: DARK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        {p.fullName || "Anónimo"}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#8899AA", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.document ? `${p.documentType || ""} ${p.document}` : "Sin documento"}
                      </Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                        {p.role && <Chip label={p.role} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${DARK}11`, color: DARK }} />}
                        {p.isGuarantor && <Chip label="Avalista" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${GREEN}18`, color: GREEN }} />}
                      </Stack>
                    </Box>
                    <Stack direction="row" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <ImpersonateIcon referencedUser={p.userId || ""} />
                    </Stack>
                  </Stack>
                  <Button
                    fullWidth size="small" variant="outlined"
                    onClick={(e) => { e.stopPropagation(); openView(p); loadUserCredits(p.document || ""); }}
                    sx={{ mt: 1.5, borderRadius: "50px", textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: `${GREEN}44`, color: GREEN }}
                  >
                    Ver detalle
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {profiles.length === 0 && !loading && (
            <Grid item xs={12}>
              <Typography sx={{ color: "#8899AA", textAlign: "center", py: 4 }}>No hay perfiles para mostrar.</Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Dialog detalle */}
      <Dialog open={!!viewing} onClose={closeView} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        {viewing && (
          <DialogContent sx={{ p: 0 }}>
            {/* Header oscuro */}
            <Box sx={{ bgcolor: DARK, p: 3, textAlign: "center", position: "relative" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: "absolute", top: 12, left: 12, right: 12 }}>
                <IconButton size="small" onClick={closeView} sx={{ color: "white", bgcolor: "#ffffff18" }}>
                  <CancelIcon fontSize="small" />
                </IconButton>
                <Stack direction="row" spacing={1}>
                  <IconButton size="small" onClick={() => { closeView(); openEdit(viewing); }} sx={{ color: "white", bgcolor: "#ffffff18" }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <ImpersonateIcon referencedUser={viewing.userId || ""} />
                </Stack>
              </Stack>

              <Box sx={{ position: "relative", display: "inline-block", mb: 1.5, mt: 2 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: `${GREEN}22`, border: `3px solid ${GREEN}`, color: GREEN, fontWeight: 800, fontSize: 28 }}>
                  {getInitials(viewing.fullName)}
                </Avatar>
                {viewing.isVerified && (
                  <CheckCircleIcon sx={{ position: "absolute", bottom: 0, right: 0, fontSize: 22, color: GREEN, bgcolor: DARK, borderRadius: "50%" }} />
                )}
              </Box>

              <Typography sx={{ fontWeight: 800, fontSize: 20, color: "white", mb: 1 }}>
                {viewing.fullName || "Anónimo"}
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                <Chip label={viewing.role || "USER"} size="small" sx={{ bgcolor: "#ffffff22", color: "white", fontWeight: 600, fontSize: 11 }} />
                <Chip label={viewing.isVerified ? "Verificado" : "No verificado"} size="small" sx={{ bgcolor: viewing.isVerified ? `${GREEN}33` : "#ffffff11", color: viewing.isVerified ? GREEN : "#8899AA", fontWeight: 600, fontSize: 11, border: `1px solid ${viewing.isVerified ? GREEN : "#ffffff22"}` }} />
                <Chip label={viewing.isGuarantor ? "Avalista" : "Sin avalista"} size="small" sx={{ bgcolor: "#ffffff11", color: "#8899AA", fontWeight: 600, fontSize: 11 }} />
              </Stack>
            </Box>

            <Box sx={{ p: 2, bgcolor: "#f7f9f7" }}>
              {/* Info personal */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1.5 }}>
                Información personal
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {[
                  { icon: <BadgeOutlined sx={{ fontSize: 18, color: GREEN }} />, label: "Documento", value: viewing.document ? `${viewing.documentType || ""} ${viewing.document}` : "Sin registrar" },
                  { icon: <EmailOutlined sx={{ fontSize: 18, color: "#f59e0b" }} />, label: "Email", value: viewing.emailOptional || "Sin registrar" },
                  { icon: <CalendarMonthOutlined sx={{ fontSize: 18, color: "#60a5fa" }} />, label: "Nacimiento", value: viewing.birthDate || "Sin registrar" },
                  { icon: <HomeOutlined sx={{ fontSize: 18, color: "#8899AA" }} />, label: "Dirección", value: viewing.address || "Sin registrar" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, bgcolor: "white", borderRadius: 2 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "#f7f9f7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#8899AA", textTransform: "uppercase", letterSpacing: 1 }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: item.value === "Sin registrar" ? "#8899AA" : DARK, fontStyle: item.value === "Sin registrar" ? "italic" : "normal" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              {/* Archivos */}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1.5 }}>
                Archivos
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { label: "Documento", Icon: Description, url: viewing.docPhotoURL },
                  { label: "Selfie", Icon: CameraAlt, url: viewing.selfiePhotoURL },
                ].map((file) => (
                  <Grid item xs={6} key={file.label}>
                    <Box
                      component={file.url ? "a" : "div"}
                      href={file.url || undefined}
                      target="_blank"
                      sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: file.url ? DARK : "#e0e0e0", borderRadius: 3, p: 2, gap: 0.5, textDecoration: "none", cursor: file.url ? "pointer" : "default", minHeight: 80 }}
                    >
                      <file.Icon sx={{ fontSize: 28, color: file.url ? "white" : "#8899AA" }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: file.url ? "white" : "#8899AA" }}>{file.label}</Typography>
                      <Typography sx={{ fontSize: 10, color: file.url ? "#aaaaaa" : "#cccccc" }}>{file.url ? "Ver archivo" : "Sin subir"}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Créditos */}
              {loadingCredits ? (
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: GREEN }} />
                </Box>
              ) : userCredits.length > 0 && (
                <>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1.5 }}>
                    Créditos
                  </Typography>

                  {/* Stats */}
                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={4}>
                      <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.2, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Total</Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: DARK }}>{userCredits.length}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Box sx={{ bgcolor: `${GREEN}11`, borderRadius: 2, p: 1.2, textAlign: "center", border: `1px solid ${GREEN}33` }}>
                        <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Total prestado</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: GREEN }}>
                          {fmtMoney(userCredits.reduce((sum, c) => sum + (c.amount ?? 0), 0))}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: "#f59e0b11", borderRadius: 2, p: 1.2, textAlign: "center", border: "1px solid #f59e0b33" }}>
                        <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Total intereses pagados</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#f59e0b" }}>
                          {fmtMoney(
                            userCredits.reduce((sum, c) =>
                              sum + (c.installments?.filter((i: any) => i.installmentStatus === "APPROVED")
                                .reduce((s: number, i: any) => s + (i.interestAmount ?? 0), 0) ?? 0), 0)
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Historial */}
                  <Stack spacing={1} sx={{ mb: 1.5 }}>
                    {userCredits.map((c) => (
                      <Box key={c.id} sx={{ bgcolor: "white", borderRadius: 2, p: 1.2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: DARK }}>{fmtMoney(c.amount)}</Typography>
                          <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{c.termMonths} meses · {c.interestRate}%</Typography>
                        </Box>
                        <Chip
                          label={c.status === "APPROVED" ? "Activo" : c.status === "FINISHED" ? "Finalizado" : "Pendiente"}
                          size="small"
                          sx={{
                            height: 20, fontSize: 10, fontWeight: 700,
                            bgcolor: c.status === "APPROVED" ? `${GREEN}18` : c.status === "FINISHED" ? "#8899AA18" : "#f59e0b18",
                            color: c.status === "APPROVED" ? GREEN : c.status === "FINISHED" ? "#8899AA" : "#f59e0b",
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              {/* Botón nuevo crédito */}
              <Button
                fullWidth variant="contained"
                startIcon={<Add />} onClick={() => { setSelectedClient(viewing); setOpenNewCredit(true); }}
                sx={{ bgcolor: GREEN, color: "white", fontWeight: 700, borderRadius: "50px", py: 1.2, textTransform: "none", fontSize: 13, boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
              >
                Nuevo crédito
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Dialog nuevo crédito desde admin */}
      <Dialog open={openNewCredit} onClose={() => setOpenNewCredit(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        <Box sx={{ bgcolor: DARK, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>Nuevo crédito</Typography>
            <Typography sx={{ fontSize: 12, color: "#8899AA" }}>{viewing?.fullName}</Typography>
          </Box>
          <IconButton onClick={() => setOpenNewCredit(false)} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2, bgcolor: "#f7f9f7" }}>
          <Stack spacing={2}>
            {/* Monto */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Monto</Typography>
              <TextField fullWidth placeholder="$ 0" value={newCreditForm.amount ? newCreditForm.amount.toLocaleString("es-CO") : ""}
                onChange={(e) => setNewCreditForm((p: any) => ({ ...p, amount: Number(e.target.value.replace(/\D/g, "")) }))}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 20, fontWeight: 800 } }} />
            </Box>

            {/* Plazo */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Plazo</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1 }}>
                <IconButton size="small" onClick={() => setNewCreditForm((p: any) => ({ ...p, termMonths: Math.max(1, p.termMonths - 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
                  <Typography sx={{ fontWeight: 800, px: 0.5 }}>−</Typography>
                </IconButton>
                <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 20, color: DARK }}>{newCreditForm.termMonths} meses</Typography>
                <IconButton size="small" onClick={() => setNewCreditForm((p: any) => ({ ...p, termMonths: Math.min(84, p.termMonths + 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
                  <Typography sx={{ fontWeight: 800, px: 0.5 }}>+</Typography>
                </IconButton>
              </Box>
            </Box>

            {/* Tasa de interés */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Tasa de interés mensual (%)</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1 }}>
                <IconButton size="small" onClick={() => setNewCreditForm((p: any) => ({ ...p, interestRate: Math.max(0, Number((p.interestRate - 0.5).toFixed(1))) }))} sx={{ bgcolor: "#f0f0f0" }}>
                  <Typography sx={{ fontWeight: 800, px: 0.5 }}>−</Typography>
                </IconButton>
                <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 20, color: GREEN }}>{newCreditForm.interestRate.toFixed(1)}%</Typography>
                <IconButton size="small" onClick={() => setNewCreditForm((p: any) => ({ ...p, interestRate: Number((p.interestRate + 0.5).toFixed(1)) }))} sx={{ bgcolor: "#f0f0f0" }}>
                  <Typography sx={{ fontWeight: 800, px: 0.5 }}>+</Typography>
                </IconButton>
              </Box>
            </Box>

            {/* Tipo de pago */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Tipo de pago</Typography>
              <Stack direction="row" spacing={1}>
                {[{ value: "CAPITAL_INTEREST", label: "Capital + Interés" }, { value: "INTEREST_ONLY", label: "Solo interés" }].map((opt) => (
                  <Box key={opt.value} onClick={() => setNewCreditForm((p: any) => ({ ...p, paymentType: opt.value }))}
                    sx={{ flex: 1, p: 1.5, borderRadius: 2, textAlign: "center", cursor: "pointer", border: `2px solid ${newCreditForm.paymentType === opt.value ? GREEN : "#e0e0e0"}`, bgcolor: newCreditForm.paymentType === opt.value ? `${GREEN}15` : "white", transition: "all 0.2s" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: newCreditForm.paymentType === opt.value ? GREEN : "#667788" }}>{opt.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Propósito */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Propósito</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {["Compra de vehículo", "Remodelación", "Estudios", "Pago de deudas", "Viaje", "Otros"].map((p) => (
                  <Box key={p} onClick={() => setNewCreditForm((prev: any) => ({ ...prev, purpose: p }))}
                    sx={{ px: 2, py: 0.8, borderRadius: "50px", cursor: "pointer", border: `2px solid ${newCreditForm.purpose === p ? GREEN : "#e0e0e0"}`, bgcolor: newCreditForm.purpose === p ? `${GREEN}15` : "white", color: newCreditForm.purpose === p ? GREEN : "#667788", fontWeight: newCreditForm.purpose === p ? 700 : 500, fontSize: 13, transition: "all 0.2s" }}>
                    {p}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Info bancaria */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Información bancaria</Typography>
              <Stack spacing={1.5}>
                <TextField select fullWidth label="Banco" value={newCreditForm.accountBank}
                  onChange={(e) => setNewCreditForm((p: any) => ({ ...p, accountBank: e.target.value }))}
                  sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                  {["Bancolombia", "Davivienda", "Nequi", "Daviplata", "Otros"].map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </TextField>
                <TextField select fullWidth label="Tipo de cuenta" value={newCreditForm.accountType}
                  onChange={(e) => setNewCreditForm((p: any) => ({ ...p, accountType: e.target.value }))}
                  sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                  {["Ahorros", "Corriente", "No aplica", "Otro"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField fullWidth label="Número de cuenta" value={newCreditForm.accountNumber}
                  onChange={(e) => setNewCreditForm((p: any) => ({ ...p, accountNumber: e.target.value }))}
                  sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Stack>
            </Box>

            {/* Avalista */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Avalista (opcional)</Typography>
              <Stack spacing={1.5}>
                <TextField fullWidth placeholder="Nombre del avalista" value={newCreditForm.guarantorName}
                  onChange={(e) => setNewCreditForm((p: any) => ({ ...p, guarantorName: e.target.value }))}
                  sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                <TextField fullWidth placeholder="Teléfono del avalista" value={newCreditForm.guarantorPhone}
                  onChange={(e) => setNewCreditForm((p: any) => ({ ...p, guarantorPhone: e.target.value }))}
                  sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1.5, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
          <Button fullWidth onClick={() => setOpenNewCredit(false)} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={handleCrearCredito} disabled={savingCredit}
            sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, color: "white", boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}>
            {savingCredit ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Crear solicitud"}
          </Button>
        </Box>
      </Dialog>

      {/* Diálogo edición */}
      <EditProfileDialog open={!!editing} profile={editing} onClose={closeEdit} onSave={saveEdit} saving={saving} roles={roles} />
    </Box>
  );
};

export default AllProfilePage;

type EditDialogProps = {
  open: boolean;
  profile: SoulDataModel | null;
  onClose: () => void;
  onSave: (changes: Partial<SoulDataModel>) => Promise<void> | void;
  saving: boolean;
  roles: string[];
};

const EditProfileDialog: React.FC<EditDialogProps> = ({ open, profile, onClose, onSave, saving, roles }) => {
  const [form, setForm] = useState<Partial<SoulDataModel>>({});

  useEffect(() => {
    setForm(profile ? {
      fullName: profile.fullName, role: profile.role, documentType: profile.documentType,
      document: profile.document, address: profile.address, emailOptional: profile.emailOptional,
      birthDate: profile.birthDate, isVerified: profile.isVerified, isGuarantor: profile.isGuarantor, userId: profile.userId,
      phoneOptional: profile.phoneOptional,
    } : {});
  }, [profile]);

  const set = (key: keyof SoulDataModel) => (e: any) => {
    const value = e && e.target && typeof e.target.value !== "undefined" ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (!profile) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ bgcolor: DARK, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>Editar perfil</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
          <CancelIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3, bgcolor: "#f7f9f7" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Nombre completo" fullWidth value={form.fullName ?? ""} onChange={set("fullName")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Rol" fullWidth value={form.role ?? ""} onChange={set("role")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              {roles.map((r) => <MenuItem value={r} key={r}>{r}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label="Nacimiento" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.birthDate ?? ""} onChange={set("birthDate")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Tipo doc." fullWidth value={form.documentType ?? ""} onChange={set("documentType")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Documento" fullWidth value={form.document ?? ""} onChange={set("document")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Teléfono de contacto (WhatsApp)" fullWidth placeholder="3001234567 · vacío = no cambiar" value={form.phoneOptional ?? ""} onChange={set("phoneOptional")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Email" type="email" fullWidth value={form.emailOptional ?? ""} onChange={set("emailOptional")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Dirección" fullWidth value={form.address ?? ""} onChange={set("address")} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Grid>
          {[
            { label: "Verificado", key: "isVerified" as keyof SoulDataModel, value: form.isVerified },
            { label: "Avalista", key: "isGuarantor" as keyof SoulDataModel, value: form.isGuarantor },
          ].map((item) => (
            <Grid item xs={6} key={item.label}>
              <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${item.value ? GREEN + "44" : "#e0e0e0"}` }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: item.value ? GREEN : DARK }}>{item.label}</Typography>
                <Switch checked={!!item.value} onChange={(_, checked) => set(item.key)(checked)} sx={{ "& .MuiSwitch-thumb": { bgcolor: item.value ? GREEN : undefined } }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", gap: 1.5 }}>
        <Button fullWidth onClick={onClose} disabled={saving} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
          Cancelar
        </Button>
        <Button fullWidth variant="contained" onClick={() => { const d = (form.phoneOptional ?? "").replace(/\D/g, ""); onSave({ ...form, phoneOptional: d ? (d.length === 10 ? "+57" + d : "+" + d) : undefined }); }} disabled={saving} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, "&:hover": { bgcolor: "#00b048" }, boxShadow: "none" }}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </Box>
    </Dialog>
  );
};
